/**
 * Brokers Tab
 * Management of Supported Brokers reference data via API.
 */

import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Tooltip,
    Stack,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { api } from '@shared/api/client';
import type { BrokerMetadataResponse } from '@shared/api/generated/api';

interface BrokerFormData {
    broker_type: string;
    display_name: string;
    auth_method: string;
    description: string;
    website_url: string;
    logo_url: string;
    is_active: boolean;
}

const emptyForm: BrokerFormData = {
    broker_type: '',
    display_name: '',
    auth_method: 'oauth',
    description: '',
    website_url: '',
    logo_url: '',
    is_active: true
};

export function BrokersTab() {
    const [brokers, setBrokers] = useState<BrokerMetadataResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState<BrokerFormData>(emptyForm);
    const [formError, setFormError] = useState<string | null>(null);

    const loadBrokers = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await api.brokerAccountsSupportedBrokers();
            setBrokers(res.data?.brokers ?? []);
        } catch {
            setError('Failed to load supported brokers.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadBrokers(); }, []);

    const handleOpenCreate = () => {
        setFormData(emptyForm);
        setFormError(null);
        setDialogOpen(true);
    };

    const handleToggleStatus = async (brokerType: string, currentStatus: boolean) => {
        try {
            setError(null);
            await (api as any).updateBrokerStatus(brokerType, { is_active: !currentStatus });
            await loadBrokers();
        } catch {
            setError('Failed to update broker status.');
        }
    };

    const handleSave = async () => {
        if (!formData.broker_type.trim() || !formData.display_name.trim() || !formData.auth_method.trim()) {
            setFormError('Type, Name, and Auth Method are required.');
            return;
        }
        try {
            setSaving(true);
            setFormError(null);
            await (api as any).createSupportedBroker(formData);
            setDialogOpen(false);
            await loadBrokers();
        } catch (e: any) {
            setFormError(e.response?.data?.detail || 'Failed to save broker. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            {/* Toolbar */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#04377b' }}>
                    Supported Brokers ({brokers.length})
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={loadBrokers} disabled={loading}
                            sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: '50%', width: 30, height: 30 }}>
                            <RefreshIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                        sx={{ bgcolor: '#04377b', '&:hover': { bgcolor: '#032e64' }, textTransform: 'none' }}
                    >
                        New Broker
                    </Button>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: '#04377b' }} />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={0} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(4, 55, 123, 0.04)' }}>
                                {['Type', 'Name', 'Auth Method', 'Status'].map((h) => (
                                    <TableCell key={h} sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {brokers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        No brokers found. Click "New Broker" to create one.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                brokers.map((broker) => (
                                    <TableRow
                                        key={broker.broker_type}
                                        sx={{
                                            '&:nth-of-type(odd)': { bgcolor: 'rgba(4, 55, 123, 0.015)' },
                                            '&:hover': { bgcolor: 'rgba(4, 55, 123, 0.03)' },
                                        }}
                                    >
                                        <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.8125rem' }}>{broker.broker_type}</TableCell>
                                        <TableCell sx={{ py: 1, fontSize: '0.75rem' }}>{broker.display_name}</TableCell>
                                        <TableCell sx={{ py: 1, fontSize: '0.75rem', color: 'text.secondary' }}>{broker.auth_method}</TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={broker.is_active ?? false}
                                                        onChange={() => handleToggleStatus(broker.broker_type as string, broker.is_active as boolean)}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                }
                                                label={<Typography variant="caption">{broker.is_active ? 'Active' : 'Inactive'}</Typography>}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: '#04377b' }}>New Broker</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <TextField
                            label="Broker Type (ID)"
                            value={formData.broker_type}
                            onChange={(e) => setFormData((f) => ({ ...f, broker_type: e.target.value.toLowerCase() }))}
                            fullWidth
                            size="small"
                            placeholder="e.g. zerodha"
                            required
                        />
                        <TextField
                            label="Display Name"
                            value={formData.display_name}
                            onChange={(e) => setFormData((f) => ({ ...f, display_name: e.target.value }))}
                            fullWidth
                            size="small"
                            required
                        />
                        <TextField
                            select
                            label="Auth Method"
                            value={formData.auth_method}
                            onChange={(e) => setFormData((f) => ({ ...f, auth_method: e.target.value }))}
                            fullWidth
                            size="small"
                            required
                        >
                            <MenuItem value="oauth">OAuth</MenuItem>
                            <MenuItem value="totp_automated">TOTP Automated</MenuItem>
                        </TextField>
                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData((f) => ({ ...f, is_active: e.target.checked }))}
                                    size="small"
                                />
                            }
                            label={<Typography variant="body2">Active</Typography>}
                        />
                        {formError && <Alert severity="error" sx={{ py: 0 }}>{formError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
                        sx={{ bgcolor: '#04377b', '&:hover': { bgcolor: '#032e64' } }}
                    >
                        {saving ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
